import { isNil } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { InternalError } from '#common/types';
import { TaggedMutex } from '#common/util';
import { FileStoreStats } from '#file-store/types';
export const createFileStore = (fileStoreWasm, fileNodeManager, blockStoreStack) => {
    return new FileStoreImpl(fileStoreWasm, fileNodeManager, blockStoreStack);
};
class FileStoreImpl {
    fileStoreWasm;
    fileNodeManager;
    logger = Logging.getLogger('FileStore');
    blockStore;
    statistics;
    perFileMutex = new TaggedMutex();
    constructor(fileStoreWasm, fileNodeManager, blockStoreStack) {
        this.fileStoreWasm = fileStoreWasm;
        this.fileNodeManager = fileNodeManager;
        const blockStoresStats = new Map();
        for (const blockStore of blockStoreStack) {
            const stats = blockStore.stats();
            blockStoresStats.set(stats.name, stats);
        }
        this.statistics = new FileStoreStats(blockStoresStats, this.logger);
        this.blockStore = blockStoreStack[0];
    }
    async get(inodeId, offset, size, cryptoKey) {
        return withStats(this.statistics.get, async () => {
            const getOperation = await this.createGetOperation(inodeId, offset, size, cryptoKey);
            const blocks = await this.getAllBlocks(getOperation.request);
            return await this.fileStoreWasm.processGetResult(inodeId, getOperation.operation, offset, size, getOperation.request.startBlockId, blocks);
        }, { inodeId, offset, size });
    }
    async put(inodeId, offset, data, cryptoKey) {
        return withStats(this.statistics.put, () => this.perFileMutex.runExclusive(inodeId, () => {
            if (data.length === 0) {
                return this.putEmpty(inodeId, offset, cryptoKey);
            }
            return this.putBlocks(inodeId, offset, data, cryptoKey);
        }), { inodeId, offset, size: data.length });
    }
    async stop() {
        await this.blockStore.stop();
    }
    stats() {
        return this.statistics;
    }
    async createGetOperation(inodeId, offset, bytesToRead, cryptoKey) {
        if (bytesToRead < 0) {
            throw new InternalError('Bytes to read should not be a negative value');
        }
        const result = await this.fileStoreWasm.copyCachedAndPrepareGetRequest(inodeId, offset, bytesToRead, cryptoKey, await this.fileNodeManager.getMetadata(inodeId));
        // we don't use any FileStore cache, we never expect to have nil get request
        if (isNil(result.request)) {
            throw new InternalError('Received an unexpectedly empty GetRequest.');
        }
        return result;
    }
    async putEmpty(inodeId, offset, cryptoKey) {
        const putOperation = await this.fileStoreWasm.prepareEmptyPutOperation(inodeId, offset, cryptoKey);
        await this.blockStore.put(putOperation.request);
        await this.updateGlobalMetadataAfterPut(inodeId, putOperation.operation);
    }
    async putBlocks(inodeId, offset, data, cryptoKey) {
        const boundaryBlocks = await this.fetchPartialBoundaryBlocks(inodeId, offset, data.length, cryptoKey);
        const putOperation = await this.fileStoreWasm.preparePutWithBlocksOperation(inodeId, offset, data, cryptoKey, boundaryBlocks);
        this.logger.debug(`Executing put with blocks of size ${putOperation.request.blocks.length}`);
        await this.blockStore.put(putOperation.request);
        await this.updateGlobalMetadataAfterPut(inodeId, putOperation.operation);
    }
    async fetchPartialBoundaryBlocks(inodeId, offset, size, cryptoKey) {
        const partialBoundaryBlocks = await this.fileStoreWasm.getPartialBoundaryBlocks(inodeId, offset, size, cryptoKey);
        const schedulesGetRequests = this.scheduleGetRequests(partialBoundaryBlocks.blocksToFetch);
        const blocksArray = await Promise.all(schedulesGetRequests);
        return FileStoreImpl.flattenBlocksArray(blocksArray);
    }
    scheduleGetRequests(blocksToFetch) {
        return blocksToFetch.map(async (request) => await this.getAllBlocks(request));
    }
    async getAllBlocks(request) {
        const blocksPromises = await this.blockStore.get(request);
        const blocks = await Promise.all(blocksPromises);
        return blocks;
    }
    updateGlobalMetadataAfterPut(inodeId, operation) {
        this.fileNodeManager.invalidateCache(inodeId);
        this.logger.debug(`Updating global metadata: ${JSON.stringify(operation)}`);
        return this.fileStoreWasm.updateGlobalMetadataAfterPut(inodeId, operation);
    }
    static flattenBlocksArray(blocksArray) {
        if (blocksArray.length === 0) {
            return new Array();
        }
        const blocks = blocksArray.reduce((accumulator, current) => accumulator.concat(current));
        if (blocks.length > 2) {
            throw new InternalError(`Fetch too many partial boundary blocks: ${blocks.length}`);
        }
        return blocks;
    }
}
